import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, Clock, MapPin, UserX, Shirt, HardHat, Cigarette,
  Coffee, ShieldAlert, MessageSquare, Save, Settings, RefreshCw,
  Bot, Hand, Info
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Icônes par type
const TYPE_ICONS = {
  retard: Clock,
  geo_hors_zone: MapPin,
  absence_non_justifiee: UserX,
  oubli_punch: Clock,
  port_epi_non_conforme: HardHat,
  vetements_non_conformes: Shirt,
  cigarette: Cigarette,
  pause_non_respectee: Coffee,
  mesures_securite_non_conformes: ShieldAlert,
  attitude: MessageSquare,
  autre: AlertTriangle,
};

// Couleurs badge
const COULEUR_CLASSES = {
  red: "bg-red-100 text-red-700 border-red-300",
  orange: "bg-orange-100 text-orange-700 border-orange-300",
  amber: "bg-amber-100 text-amber-700 border-amber-300",
  gray: "bg-gray-100 text-gray-700 border-gray-300",
  blue: "bg-blue-100 text-blue-700 border-blue-300",
};

// Labels paramètres techniques pour les règles auto
const PARAM_LABELS = {
  delai_tolerance_minutes: { label: "Délai de tolérance", unit: "min", desc: "Avertissement si punch après ce délai" },
  rayon_metres: { label: "Rayon acceptable", unit: "m", desc: "Avertissement si punch à plus de cette distance du chantier" },
  heures_apres_debut: { label: "Heures après début cédulé", unit: "h", desc: "Avertissement si pas de punch après ce délai" },
};

const RuleCard = ({ config, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState({ ...config });
  const [saving, setSaving] = useState(false);

  const Icon = TYPE_ICONS[config.type] || AlertTriangle;
  const isAuto = config.categorie_type === "auto";

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${api.baseUrl}/avertissements-auto/categories/config`, local);
      if (res.status === 200) {
        toast.success(`✅ Règle "${local.label}" sauvegardée`);
        setEditing(false);
        onSave();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActif = async (checked) => {
    const updated = { ...local, actif: checked };
    setLocal(updated);
    try {
      await axios.post(`${api.baseUrl}/avertissements-auto/categories/config`, updated);
      toast.success(`Règle ${checked ? "activée" : "désactivée"}`);
      onSave();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <Card className={`transition-all ${!local.actif ? "opacity-60" : ""}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          {/* Left: icon + info */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg shrink-0 ${isAuto ? "bg-blue-50" : "bg-purple-50"}`}>
              <Icon className={`w-5 h-5 ${isAuto ? "text-blue-600" : "text-purple-600"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800">{local.label}</span>
                <Badge variant="outline" className={`text-xs ${isAuto ? "border-blue-300 text-blue-700" : "border-purple-300 text-purple-700"}`}>
                  {isAuto ? <><Bot className="w-3 h-3 mr-1 inline" />Auto</> : <><Hand className="w-3 h-3 mr-1 inline" />Manuel</>}
                </Badge>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${COULEUR_CLASSES[local.couleur] || COULEUR_CLASSES.gray}`}>
                  ● {local.couleur}
                </span>
              </div>

              {editing ? (
                <div className="mt-3 space-y-3">
                  {/* Seuil max bonus */}
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-slate-600 w-48 shrink-0">
                      Seuil max (inéligible au bonus)
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={local.seuil_max_bonus}
                        onChange={e => setLocal(p => ({ ...p, seuil_max_bonus: parseInt(e.target.value) || 1 }))}
                        className="w-20 h-8 text-sm"
                      />
                      <span className="text-sm text-slate-500">avertissement(s)</span>
                    </div>
                  </div>

                  {/* Paramètres techniques (auto seulement) */}
                  {isAuto && local.parametres && Object.keys(local.parametres).length > 0 && (
                    <div className="border-t pt-3 mt-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Déclenchement automatique</p>
                      {Object.entries(local.parametres).map(([key, val]) => {
                        const meta = PARAM_LABELS[key] || { label: key, unit: "", desc: "" };
                        return (
                          <div key={key} className="flex items-center gap-3 mb-2">
                            <label className="text-sm text-slate-600 w-48 shrink-0">{meta.label}</label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={1}
                                value={val}
                                onChange={e => setLocal(p => ({
                                  ...p,
                                  parametres: { ...p.parametres, [key]: parseInt(e.target.value) || 1 }
                                }))}
                                className="w-24 h-8 text-sm"
                              />
                              <span className="text-sm text-slate-500">{meta.unit}</span>
                            </div>
                            {meta.desc && <span className="text-xs text-slate-400">{meta.desc}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Notifications */}
                  <div className="border-t pt-3 flex gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={local.notification_email}
                        onCheckedChange={v => setLocal(p => ({ ...p, notification_email: v }))}
                      />
                      <span className="text-sm text-slate-600">Email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={local.notification_sms}
                        onCheckedChange={v => setLocal(p => ({ ...p, notification_sms: v }))}
                      />
                      <span className="text-sm text-slate-600">SMS</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-4 flex-wrap">
                  <span className="text-sm text-slate-500">
                    Inéligible au bonus à partir de{" "}
                    <strong className="text-slate-700">{local.seuil_max_bonus}</strong> avertissement(s)
                  </span>
                  {isAuto && local.parametres && Object.keys(local.parametres).map(key => {
                    const meta = PARAM_LABELS[key] || { label: key, unit: "" };
                    return (
                      <span key={key} className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                        {meta.label}: <strong>{local.parametres[key]}</strong> {meta.unit}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: controls */}
          <div className="flex items-center gap-2 shrink-0">
            <Switch
              checked={local.actif}
              onCheckedChange={handleToggleActif}
            />
            {editing ? (
              <>
                <Button size="sm" variant="outline" onClick={() => { setLocal({ ...config }); setEditing(false); }}>
                  Annuler
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? "..." : "Sauvegarder"}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Settings className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const ConfigurationAvertissements = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | auto | manuel

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${api.baseUrl}/avertissements-auto/categories/config`);
      const data = res.data;
      setConfigs(data.configs || []);
    } catch {
      toast.error("Erreur chargement des règles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const autoConfigs = configs.filter(c => c.categorie_type === "auto");
  const manuelConfigs = configs.filter(c => c.categorie_type === "manuel");
  const filteredConfigs = filter === "auto" ? autoConfigs : filter === "manuel" ? manuelConfigs : configs;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Règles Avertissements</h1>
          <p className="text-slate-500 mt-1">
            Configurez les seuils et paramètres de tous vos avertissements — le système utilise ces règles en temps réel.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Info banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-800 space-y-1">
              <p className="font-semibold">🔗 Système connecté en temps réel</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                <li><strong>Avertissements Auto</strong> <span className="text-blue-500">(🤖)</span> — créés automatiquement lors des pointages (retard, géoloc, absence)</li>
                <li><strong>Avertissements Manuels</strong> <span className="text-blue-500">(✋)</span> — donnés manuellement par un gestionnaire</li>
                <li>Le <strong>seuil max</strong> détermine à partir de combien d'avertissements l'employé devient <strong>inéligible au bonus</strong></li>
                <li>Tout changement ici est appliqué <strong>immédiatement</strong> au système</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtres + Compteurs */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
        >
          Toutes ({configs.length})
        </Button>
        <Button
          variant={filter === "auto" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("auto")}
        >
          <Bot className="w-4 h-4 mr-1" /> Automatiques ({autoConfigs.length})
        </Button>
        <Button
          variant={filter === "manuel" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("manuel")}
        >
          <Hand className="w-4 h-4 mr-1" /> Manuelles ({manuelConfigs.length})
        </Button>
      </div>

      {/* Liste des règles */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
          Chargement des règles...
        </div>
      ) : filteredConfigs.length === 0 ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-slate-500">
            Aucune règle trouvée
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredConfigs.map(config => (
            <RuleCard key={config.type} config={config} onSave={loadData} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ConfigurationAvertissements;
